/**
 * The interactive board that ships on content pages.
 *
 * This is deliberately *not* the app's BoardView. That one paints photoreal
 * walnut and 100 KB piece renders, which is right for a game client and wrong
 * for a page whose job is to load in under a second and be readable at 320 px.
 * Here the board is drawn — pure SVG, a few hundred bytes of geometry, crisp at
 * any size, correct in both themes, and legible when printed.
 *
 * What it is *not* is a decoration. Legality comes from @xq/rules, the same
 * engine the game runs on, so a page that says "the horse cannot jump this
 * piece" is demonstrating the claim rather than asserting it.
 *
 * Progressive enhancement: the page ships a static SVG diagram of the position
 * in the HTML. This script upgrades it in place. No JS, no upgrade, still a
 * correct diagram — which is exactly what a crawler that doesn't run scripts
 * gets to read.
 *
 * Modes, declared as `data-mode` on the mount:
 *   diagram   — a still position (default)
 *   explore   — move the pieces freely; illegal moves are refused with a reason
 *   sequence  — step through a scripted line, with commentary per move
 *   challenge — find a specific move; wrong tries are explained, not buzzed
 */

import {
  Color,
  PieceType,
  coordToSquare,
  inCheck,
  isCheckmate,
  legalMoves,
  moveToUci,
  parseFen,
  squareToCoord,
  type Move,
  type Position,
  type Undo,
} from "@xq/rules";

const NS = "http://www.w3.org/2000/svg";

/* Geometry: 9 files x 10 ranks on a 900x1000 field, plus gutters. */
/* Margin, and it has to clear the piece radius with room to spare. At 46 against
   a radius of 39 there were seven units of air, which renders as about four
   pixels: every piece on an outer file looked like it was sitting on the frame.
   58 gives nineteen, which is the difference between a board and a diagram. */
const M = 58;              // margin
const GX = 96;             // horizontal gap between files
const GY = 96;             // vertical gap between ranks
const W = M * 2 + GX * 8;
const H = M * 2 + GY * 9;
const R = 39;              // piece radius

const fileX = (f: number) => M + f * GX;
const rankY = (r: number) => M + (9 - r) * GY;   // rank 0 at the bottom (red home)

/**
 * Chinese glyphs, indexed by PieceType. Red and Black write the same piece with
 * different characters — that asymmetry is part of the game, not a style choice.
 */
const GLYPH: Record<PieceType, [string, string]> = {
  [PieceType.King]:     ["帥", "將"],
  [PieceType.Advisor]:  ["仕", "士"],
  [PieceType.Elephant]: ["相", "象"],
  [PieceType.Horse]:    ["傌", "馬"],
  [PieceType.Rook]:     ["俥", "車"],
  [PieceType.Cannon]:   ["炮", "砲"],
  [PieceType.Pawn]:     ["兵", "卒"],
};

const NAME: Record<PieceType, string> = {
  [PieceType.King]: "general",
  [PieceType.Advisor]: "advisor",
  [PieceType.Elephant]: "elephant",
  [PieceType.Horse]: "horse",
  [PieceType.Rook]: "chariot",
  [PieceType.Cannon]: "cannon",
  [PieceType.Pawn]: "soldier",
};

function el<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const n = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
}

/** Square index <-> file/rank, matching @xq/rules' 0..89 layout. */
const sqFile = (sq: number) => sq % 9;
const sqRank = (sq: number) => Math.floor(sq / 9);

interface Spec {
  fen: string;
  mode: "diagram" | "explore" | "sequence" | "challenge";
  flip: boolean;
  /** sequence: UCI moves with optional commentary. */
  line: { uci: string; say?: string }[];
  /** challenge: any of these UCI moves is accepted. */
  answer: string[];
  /** challenge/explore prompt. */
  ask: string;
  /** Shown when the challenge is solved. */
  win: string;
  /** Squares to ring for emphasis (coordinates like "e4"). */
  mark: string[];
  /** Show every legal move for the piece on this square, as dots. */
  show: string | null;
}

function readSpec(mount: HTMLElement): Spec {
  const j = (name: string, fallback: unknown) => {
    const raw = mount.dataset[name];
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch { return fallback; }
  };
  return {
    fen: mount.dataset.fen || "",
    mode: (mount.dataset.mode as Spec["mode"]) || "diagram",
    flip: mount.dataset.flip === "true",
    line: j("line", []) as Spec["line"],
    answer: j("answer", []) as string[],
    ask: mount.dataset.ask || "",
    win: mount.dataset.win || "Correct.",
    mark: j("mark", []) as string[],
    show: mount.dataset.show || null,
  };
}

class Board {
  private mount: HTMLElement;
  private spec: Spec;
  private pos: Position;
  private svg: SVGSVGElement;
  private say: HTMLElement | null;
  private alt: HTMLElement | null;
  private layer: SVGGElement;
  private selected: number | null = null;
  /* Drag state. `dragFrom` is the square picked up; `dragMoved` records whether the
     pointer travelled far enough to count as a drag rather than a tap, which is what
     decides if the trailing click is suppressed. */
  private dragFrom: number | null = null;
  private dragMoved = false;
  private dragOrigin = { x: 0, y: 0 };
  /** Where the keyboard is pointing. Null until someone uses an arrow key. */
  private cursor: number | null = null;
  /** The opening prompt, so Reset can restore it instead of a stale verdict. */
  private prompt = "";
  private lastMove: Move | null = null;
  private ply = 0;              // sequence position
  private solved = false;
  private undos: { undo: Undo; prevLast: Move | null }[] = [];

  constructor(mount: HTMLElement) {
    this.mount = mount;
    this.spec = readSpec(mount);
    this.pos = parseFen(this.spec.fen);
    this.say = mount.parentElement?.querySelector<HTMLElement>(".board-say") ?? null;
    // The figure already renders the position as text, and the SVG points at
    // it. Carrying the same sentence in an aria-label as well made a screen
    // reader read every diagram out twice.
    this.alt = mount.closest("figure")?.querySelector<HTMLElement>(".diagram-alt") ?? null;

    this.svg = el("svg", {
      viewBox: `0 0 ${W} ${H}`,
      class: "xqb",
      role: "img",
    });
    if (this.alt?.id) this.svg.setAttribute("aria-labelledby", this.alt.id);
    else this.svg.setAttribute("aria-label", this.describe());
    this.layer = el("g");
    this.drawField();
    this.svg.append(this.layer);
    mount.replaceChildren(this.svg);

    if (this.spec.mode !== "diagram") {
      // Not `role="application"`: that switches a screen reader out of browse
      // mode and hands it every key, which is only honest if the widget
      // actually handles them. It now does — see onKey — but a grid of
      // labelled points is what this is, so it says so.
      this.svg.setAttribute("role", "group");
      this.svg.setAttribute("tabindex", "0");
      this.svg.setAttribute(
        "aria-description",
        "Interactive board. Arrow keys move the cursor, Enter selects and moves, Escape clears the selection.",
      );
      this.svg.addEventListener("click", (e) => this.onClick(e));
      // Drag, on top of clicking rather than instead of it: a visitor's first
      // instinct on a board is to drag a piece, and the click path still works for
      // anyone who prefers it or is using a keyboard.
      this.svg.addEventListener("pointerdown", (e) => this.onPointerDown(e));
      this.svg.addEventListener("pointermove", (e) => this.onPointerMove(e));
      this.svg.addEventListener("pointerup", (e) => this.onPointerUp(e));
      this.svg.addEventListener("pointercancel", () => this.endDrag());
      this.svg.addEventListener("keydown", (e) => this.onKey(e));
      this.svg.addEventListener("blur", () => { this.cursor = null; this.paint(); });
    }
    // The server already rendered a prompt for explore boards that declare no
    // `ask`, so take whatever is on the page as the thing to return to.
    this.prompt = this.spec.ask || this.say?.textContent?.trim() || "";
    this.paint();
    if (this.spec.ask) this.tell(this.spec.ask, "");
  }

  /* ------------------------------------------------------------ drawing */

  /** Lines, river and palaces never change, so they are drawn once. */
  private drawField() {
    const g = el("g", { "aria-hidden": "true" });
    const line = (x1: number, y1: number, x2: number, y2: number, cls = "l") =>
      g.append(el("line", { x1, y1, x2, y2, class: cls }));

    g.append(el("rect", { x: 0, y: 0, width: W, height: H, rx: 8, class: "bg" }));

    // Files stop at the river except the two edges, which run the full height.
    for (let f = 0; f < 9; f++) {
      if (f === 0 || f === 8) line(fileX(f), rankY(9), fileX(f), rankY(0));
      else { line(fileX(f), rankY(9), fileX(f), rankY(5)); line(fileX(f), rankY(4), fileX(f), rankY(0)); }
    }
    for (let r = 0; r < 10; r++) line(fileX(0), rankY(r), fileX(8), rankY(r));

    // Palace diagonals.
    for (const base of [0, 7]) {
      line(fileX(3), rankY(base), fileX(5), rankY(base + 2));
      line(fileX(5), rankY(base), fileX(3), rankY(base + 2));
    }

    // The river, named as it always is on a real board. Two anchored halves,
    // not one string with a run of spaces in it: SVG collapses that run to a
    // single space, which put the two names shoulder to shoulder in the middle.
    const riverY = (rankY(5) + rankY(4)) / 2;
    for (const [x, text] of [[W * 0.27, "楚 河"], [W * 0.73, "漢 界"]] as [number, string][]) {
      const t = el("text", { x, y: riverY, class: "river", "text-anchor": "middle", "dominant-baseline": "central" });
      t.textContent = text;
      g.append(t);
    }

    // Board edge.
    g.append(el("rect", {
      x: fileX(0), y: rankY(9), width: GX * 8, height: GY * 9,
      fill: "none", class: "edge",
    }));
    this.svg.append(g);
  }

  private paint() {
    const kids: SVGElement[] = [];
    const flip = this.spec.flip;
    const px = (sq: number) => fileX(flip ? 8 - sqFile(sq) : sqFile(sq));
    const py = (sq: number) => rankY(flip ? 9 - sqRank(sq) : sqRank(sq));

    // Last move: two soft rings, so the eye lands on the change first.
    if (this.lastMove) {
      for (const sq of [this.lastMove.from, this.lastMove.to]) {
        kids.push(el("circle", { cx: px(sq), cy: py(sq), r: R + 3, class: "last" }));
      }
    }

    for (const coord of this.spec.mark) {
      const sq = coordSq(coord);
      if (sq >= 0) kids.push(el("circle", { cx: px(sq), cy: py(sq), r: R + 5, class: "mark" }));
    }

    // Move dots — either an explicit teaching aid, or the selected piece. The
    // dots come from the real generator, so a page claiming "the horse has
    // eight moves here" is showing eight dots because there are eight.
    const from = this.selected ?? (this.spec.show ? coordSq(this.spec.show) : -1);
    const owner = from >= 0 ? this.pos.pieceAt(from) : null;
    if (owner) {
      for (const m of legalMoves(this.pos, owner.color)) {
        if (m.from !== from) continue;
        const hit = !!this.pos.pieceAt(m.to);
        kids.push(el("circle", {
          cx: px(m.to), cy: py(m.to), r: hit ? R + 2 : 11,
          class: hit ? "dot-take" : "dot",
        }));
      }
    }

    for (let sq = 0; sq < 90; sq++) {
      const p = this.pos.pieceAt(sq);
      if (!p) continue;
      const red = p.color === Color.Red;
      const g = el("g", {
        class: `pc ${red ? "red" : "black"}${this.selected === sq ? " sel" : ""}`,
        "data-sq": String(sq),
        role: "img",
        "aria-label": `${red ? "Red" : "Black"} ${NAME[p.type]} on ${squareToCoord(sq)}`,
      });
      g.append(el("circle", { cx: px(sq), cy: py(sq), r: R, class: "disc" }));
      g.append(el("circle", { cx: px(sq), cy: py(sq), r: R - 4.5, class: "ring" }));
      // Centred by the em box, not by a baseline nudge — see board-svg.mjs.
      const t = el("text", { x: px(sq), y: py(sq), class: "glyph", "text-anchor": "middle", "dominant-baseline": "central" });
      t.textContent = GLYPH[p.type][red ? 0 : 1];
      g.append(t);
      kids.push(g);
    }

    // Where the keyboard is pointing.
    if (this.cursor !== null) {
      kids.push(el("rect", {
        x: px(this.cursor) - R - 6, y: py(this.cursor) - R - 6,
        width: (R + 6) * 2, height: (R + 6) * 2, rx: 8, class: "cursor",
      }));
    }

    // The side to move being in check is the most important thing on the board,
    // so it gets the one ring that isn't subtle.
    const stm = this.pos.sideToMove;
    if (this.pos.kingSquare(stm) >= 0 && inCheck(this.pos, stm)) {
      kids.push(el("circle", { cx: px(this.pos.kingSquare(stm)), cy: py(this.pos.kingSquare(stm)), r: R + 6, class: "check" }));
    }

    this.layer.replaceChildren(...kids);
    if (this.alt) this.alt.textContent = this.describe();
    else this.svg.setAttribute("aria-label", this.describe());
  }

  /**
   * A sentence a screen reader can read out and a crawler can quote (§30, §19).
   * Naming the pieces beats "chess board image": it is the only description a
   * non-sighted reader gets of the position the paragraph is discussing.
   */
  private describe(): string {
    const side = this.pos.sideToMove === Color.Red ? "Red" : "Black";
    const say: string[] = [];
    for (const color of [Color.Red, Color.Black]) {
      const parts: string[] = [];
      for (let sq = 0; sq < 90; sq++) {
        const p = this.pos.pieceAt(sq);
        if (p && p.color === color) parts.push(`${NAME[p.type]} ${squareToCoord(sq)}`);
      }
      if (parts.length) say.push(`${color === Color.Red ? "Red" : "Black"}: ${parts.join(", ")}`);
    }
    return `Xiangqi position. ${say.join(". ")}. ${side} to move.`;
  }

  /* ------------------------------------------------------------ interaction */

  private squareAt(e: MouseEvent): number {
    const rect = this.svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * H;
    let best = -1, bestD = Infinity;
    for (let sq = 0; sq < 90; sq++) {
      const f = this.spec.flip ? 8 - sqFile(sq) : sqFile(sq);
      const r = this.spec.flip ? 9 - sqRank(sq) : sqRank(sq);
      const d = (fileX(f) - x) ** 2 + (rankY(r) - y) ** 2;
      if (d < bestD) { bestD = d; best = sq; }
    }
    return bestD <= (GX * 0.62) ** 2 ? best : -1;
  }

  /** Pixels of travel before a press counts as a drag rather than a tap. */
  private static readonly DRAG_SLOP = 6;

  private onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    const sq = this.squareAt(e);
    if (sq < 0) return;
    const piece = this.pos.pieceAt(sq);
    // Only pick up a piece that could actually move. Anything else falls through to
    // the click handler, which already explains whose turn it is.
    if (!piece || piece.color !== this.pos.sideToMove) return;
    this.dragFrom = sq;
    this.dragMoved = false;
    this.dragOrigin = { x: e.clientX, y: e.clientY };
    try { this.svg.setPointerCapture(e.pointerId); } catch { /* not all browsers */ }
  }

  private onPointerMove(e: PointerEvent) {
    if (this.dragFrom === null) return;
    const dx = e.clientX - this.dragOrigin.x;
    const dy = e.clientY - this.dragOrigin.y;
    if (!this.dragMoved) {
      if (Math.hypot(dx, dy) < Board.DRAG_SLOP) return;
      this.dragMoved = true;
      // Select on the first real movement, so the legal-move dots appear under the
      // finger exactly as they would on a click.
      if (this.selected !== this.dragFrom) { this.selected = this.dragFrom; this.paint(); }
      this.svg.classList.add("dragging");
    }
    const g = this.layer.querySelector(`[data-sq="${this.dragFrom}"]`) as SVGGElement | null;
    if (!g) return;
    // Translate in viewBox units so the piece tracks the pointer at any board size.
    const rect = this.svg.getBoundingClientRect();
    const sx = (dx / rect.width) * W;
    const sy = (dy / rect.height) * H;
    g.setAttribute("transform", `translate(${sx} ${sy})`);
    g.setAttribute("pointer-events", "none");
  }

  private onPointerUp(e: PointerEvent) {
    if (this.dragFrom === null) return;
    const from = this.dragFrom;
    const moved = this.dragMoved;
    this.endDrag();
    if (!moved) return;            // a tap: let the click handler deal with it
    const to = this.squareAt(e);
    if (to < 0 || to === from) { this.paint(); return; }
    this.selected = from;
    this.activate(to);
  }

  /** Drop the drag state and undo the visual offset, whatever the outcome. */
  private endDrag() {
    if (this.dragFrom !== null) {
      const g = this.layer.querySelector(`[data-sq="${this.dragFrom}"]`);
      if (g) { g.removeAttribute("transform"); g.removeAttribute("pointer-events"); }
    }
    if (this.dragMoved) {
      // Suppress the click that follows a real drag, or the piece would move and then
      // be reselected by the click arriving at the destination.
      this.suppressClick = true;
    }
    this.dragFrom = null;
    this.dragMoved = false;
    this.svg.classList.remove("dragging");
  }

  private suppressClick = false;

  private onClick(e: MouseEvent) {
    if (this.suppressClick) { this.suppressClick = false; return; }
    const sq = this.squareAt(e);
    if (sq < 0) { this.selected = null; this.paint(); return; }
    this.cursor = null;
    this.activate(sq);
  }

  /** Select, deselect, reselect, or move. Shared by tap and by Enter. */
  private activate(sq: number) {
    const piece = this.pos.pieceAt(sq);

    if (this.selected === null) {
      if (!piece) return;
      if (piece.color !== this.pos.sideToMove) {
        this.tell(`It is ${this.pos.sideToMove === Color.Red ? "Red" : "Black"}'s turn.`, "");
        return;
      }
      this.selected = sq;
      this.paint();
      return;
    }
    if (sq === this.selected) { this.selected = null; this.paint(); return; }
    if (piece && piece.color === this.pos.sideToMove) { this.selected = sq; this.paint(); return; }

    this.tryMove(this.selected, sq);
  }

  /**
   * The board is focusable, so it has to be operable: arrows walk a cursor
   * across the points, Enter does what a tap does, Escape backs out. Without
   * this, `tabindex="0"` was an invitation into a dead end.
   */
  private onKey(e: KeyboardEvent) {
    if (e.key === "Escape") { this.selected = null; this.cursor = null; this.paint(); return; }
    if (e.key.toLowerCase() === "z" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); this.undo(); return; }

    const STEP: Record<string, [number, number]> = {
      ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1],
    };
    const step = STEP[e.key];
    if (step) {
      e.preventDefault();
      // Arrows are read on screen, so they are applied to the *displayed*
      // file and rank and converted back — otherwise "left" goes right on a
      // flipped board.
      const start = this.cursor ?? this.selected ?? this.pos.kingSquare(this.pos.sideToMove);
      const from = start >= 0 ? start : 4;
      const flip = this.spec.flip;
      let vf = flip ? 8 - sqFile(from) : sqFile(from);
      let vr = flip ? 9 - sqRank(from) : sqRank(from);
      if (this.cursor !== null) { vf += step[0]; vr += step[1]; }
      vf = Math.max(0, Math.min(8, vf));
      vr = Math.max(0, Math.min(9, vr));
      const f = flip ? 8 - vf : vf;
      const r = flip ? 9 - vr : vr;
      this.cursor = r * 9 + f;
      this.paint();
      const p = this.pos.pieceAt(this.cursor);
      this.tell(
        p
          ? `${squareToCoord(this.cursor)}: ${p.color === Color.Red ? "Red" : "Black"} ${NAME[p.type]}.`
          : `${squareToCoord(this.cursor)}: empty.`,
        "",
      );
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (this.cursor !== null) this.activate(this.cursor);
    }
  }

  private tryMove(from: number, to: number) {
    const legal = legalMoves(this.pos).find((m) => m.from === from && m.to === to);
    if (!legal) { this.refuse(from, to); return; }

    const uci = moveToUci(legal);

    if (this.spec.mode === "challenge" && !this.solved) {
      if (this.spec.answer.includes(uci)) {
        this.commit(legal);
        this.solved = true;
        this.tell(this.spec.win, "good");
      } else {
        // §11: never just "wrong". Say what this move actually does.
        this.tell(this.critique(legal), "bad");
        this.selected = null;
        this.paint();
      }
      return;
    }
    this.commit(legal);
    this.selected = null;
  }

  /**
   * Explain a refused move using the rule that refused it, rather than a buzz.
   * The distinction that matters to a learner is *which* rule stopped them.
   */
  private refuse(from: number, to: number) {
    const p = this.pos.pieceAt(from);
    if (!p) return;
    const dest = squareToCoord(to);
    const hasAny = legalMoves(this.pos, p.color).some((m) => m.from === from);

    const WHY: Record<PieceType, string> = {
      [PieceType.Horse]: "The horse steps one point orthogonally then one diagonally, and it is blocked if the point it steps through is occupied — 蹩马腿.",
      [PieceType.Elephant]: "The elephant moves two points diagonally, never crosses the river, and is blocked if the midpoint is occupied — 塞象眼.",
      [PieceType.Advisor]: "The advisor moves one point diagonally and never leaves the palace.",
      [PieceType.King]: "The general moves one point orthogonally, stays inside the palace, and may never face the enemy general down an open file.",
      [PieceType.Cannon]: "The cannon moves like a chariot, but captures only by jumping exactly one piece — the screen.",
      [PieceType.Pawn]: "The soldier moves one point forward, and may only move sideways once it has crossed the river. It never moves backwards.",
      [PieceType.Rook]: "The chariot slides along a rank or file and cannot jump over anything.",
    };

    const why = hasAny
      ? WHY[p.type]
      : "That piece has no legal move at all here — moving it would expose your own general.";
    this.tell(`${dest} is not legal. ${why}`, "bad");
    this.selected = null;
    this.paint();
  }

  /**
   * For a legal-but-wrong challenge attempt, say what the move actually does.
   * "Wrong" teaches nothing; "that gives check but the general simply steps
   * aside" teaches the pattern (§11).
   */
  private critique(m: Move): string {
    const captured = this.pos.pieceAt(m.to);
    const undo = this.pos.makeMoveFast(m);
    const opponentToMove = this.pos.sideToMove;
    let reply: string;
    if (isCheckmate(this.pos)) {
      reply = " In fact that is mate too — but it is not the line this position is about.";
    } else if (inCheck(this.pos, opponentToMove)) {
      reply = " It gives check, but the general has an answer, and the attack runs out.";
    } else if (legalMoves(this.pos, opponentToMove).some((r) => r.to === m.to)) {
      reply = " The piece you just moved can be captured straight away.";
    } else {
      reply = " It is quiet, and it gives the defender time to consolidate.";
    }
    this.pos.unmakeMove(undo);

    const took = captured ? ` It wins the ${NAME[captured.type]},` : "";
    return `Legal, but not the idea here.${took}${reply}`;
  }

  private commit(m: Move) {
    this.undos.push({ undo: this.pos.makeMoveFast(m), prevLast: this.lastMove });
    this.lastMove = m;
    this.selected = null;
    this.paint();
  }

  private undo() {
    const step = this.undos.pop();
    if (!step) return;
    this.pos.unmakeMove(step.undo);
    this.lastMove = step.prevLast;
    this.selected = null;
    this.paint();
  }

  reset() {
    this.pos = parseFen(this.spec.fen);
    this.lastMove = null;
    this.selected = null;
    this.cursor = null;
    this.solved = false;
    this.ply = 0;
    this.undos = [];
    this.paint();
    this.tell(this.prompt, "");
    this.syncMoveButtons();
  }

  /** sequence mode: advance to ply n, replaying from the start position. */
  goto(n: number) {
    this.pos = parseFen(this.spec.fen);
    this.lastMove = null;
    this.cursor = null;
    this.undos = [];
    const target = Math.max(0, Math.min(n, this.spec.line.length));
    for (let i = 0; i < target; i++) {
      const want = this.spec.line[i]!.uci;
      const m = legalMoves(this.pos).find((mv) => moveToUci(mv) === want);
      if (!m) break;
      this.pos.makeMove(m);
      this.lastMove = m;
    }
    this.ply = target;
    this.paint();
    const step = target > 0 ? this.spec.line[target - 1] : null;
    this.tell(step?.say ?? this.prompt, "");
    this.syncMoveButtons();
  }

  step(d: number) { this.goto(this.ply + d); }

  private syncMoveButtons() {
    const bar = this.mount.parentElement?.querySelector(".board-moves");
    if (!bar) return;
    bar.querySelectorAll("button").forEach((b, i) => {
      b.setAttribute("aria-current", String(i + 1 === this.ply));
    });
  }

  private tell(msg: string, tone: string) {
    if (!this.say) return;
    this.say.textContent = msg;
    this.say.dataset.tone = tone;
  }
}

/** Squares are addressed as "e4" in copy; @xq/rules speaks the same dialect. */
function coordSq(coord: string): number {
  try {
    const sq = coordToSquare(coord);
    return sq >= 0 && sq < 90 ? sq : -1;
  } catch { return -1; }
}

/** Wire every board on the page, plus its controls. */
function init() {
  document.querySelectorAll<HTMLElement>("[data-xqboard]").forEach((mount) => {
    let board: Board;
    try { board = new Board(mount); } catch { return; } // a bad FEN must not take the page down
    const host = mount.parentElement;
    host?.querySelector("[data-act='reset']")?.addEventListener("click", () => board.reset());
    host?.querySelector("[data-act='prev']")?.addEventListener("click", () => board.step(-1));
    host?.querySelector("[data-act='next']")?.addEventListener("click", () => board.step(1));
    host?.querySelectorAll<HTMLButtonElement>(".board-moves button").forEach((b, i) => {
      b.addEventListener("click", () => board.goto(i + 1));
    });
  });
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
